import { faArrowToTop, faSync } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import type { JobType } from '@kcftech/prometheus-client';
import { useSetupToolTemplateUpload } from 'custom_hooks/data_hooks/setup_tools/useSetupToolTemplateUpload';
import { uploadFile } from 'utils/file';

export type SetupToolsTemplateUploadButtonProps = Readonly<{
    locationId: string | undefined;
    jobType: JobType;
    /** Callback for when the upload has started */
    onStart: () => void;
    /**
     * Callback for when the file has been uploaded
     */
    onComplete: (errors?: string[]) => void;
}>;

export function SetupToolsTemplateUploadButton({
    locationId,
    jobType,
    onStart,
    onComplete,
}: SetupToolsTemplateUploadButtonProps) {
    const {
        isPending: isUploading,
        mutateAsync: runSetupTool,
    } = useSetupToolTemplateUpload({
        onSuccess: () => onComplete(),
        onError: (response) => {
            onComplete(Object.values(response.Errors));
        },
    });

    const onTemplateUpload = () => {
        uploadFile(
            async (uploadedFile) => {
                if (!locationId) {
                    throw new Error('Attempted to run setup tool without location id');
                }
                onStart();
                await runSetupTool({
                    type: jobType,
                    locationId,
                    template: uploadedFile,
                });
            },
            ['.xlsx'],
        );
    };

    return (
        <Button
            appearance="primary"
            spacing="medium"
            icon={
                isUploading ? (
                    <FontAwesomeIcon icon={faSync} spin />
                ) : (
                    <FontAwesomeIcon icon={faArrowToTop} />
                )
            }
            disabled={locationId === undefined || isUploading}
            onClick={() => onTemplateUpload()}
        >
            Upload File
        </Button>
    );
}
